import { useCallback, useEffect, useRef, useState } from 'react';

import useEvent from '@/shared/hooks/useEvent';
import useMinimizedWindows from './useMinimizedWindows';
import { useModalInstanceId } from './ModalInstanceContext';

type Args = {
  icon?: React.ReactNode;
  minimizable?: boolean;
  minimizeIcon?: React.ReactNode;
  minimizeTitle?: string;
  onClose: () => void;
  open: boolean;
  title?: React.ReactNode;
};

// Shared minimize behaviour for modal primitives (KpcModal, FormModal). The
// modal stays mounted while minimized (open=false + keepMounted) so drafts
// survive. Registry transitions are explicit: minimize() and restore() flip
// the draft's status; open/closed is owned by the ModalInstanceHost when one
// is present. Without a MinimizedWindowsProvider, `canMinimize` is false and
// the modal behaves exactly as before.
const useMinimizableModal = ({
  icon,
  minimizable,
  minimizeIcon,
  minimizeTitle,
  onClose,
  open,
  title,
}: Args) => {
  const [isMinimized, setIsMinimized] = useState(false);

  // Hosted instances adopt the host's id, so the instance list, the registry
  // and the dock share one id per draft; standalone modals mint their own,
  // stable for exactly one mounted-modal lifetime.
  const instanceId = useModalInstanceId();
  const [generatedId] = useState(() => crypto.randomUUID());
  const windowId = instanceId ?? generatedId;
  const isHosted = instanceId !== undefined;

  const dock = useMinimizedWindows();
  const canMinimize = !!minimizable && !!dock;

  const getIcon = useEvent(() => minimizeIcon ?? icon);

  const fallbackTitle = minimizeTitle ?? (typeof title === 'string' ? title : '');

  // The form reports its live title (e.g. the email subject) through a ref on
  // every keystroke; none of that may re-render the modal shell, so it is not
  // state. Only minimize() reads it, as the snapshot shown on the dock pill.
  const liveTitleRef = useRef('');
  const reportTitle = useCallback((value: string) => {
    liveTitleRef.current = value;
  }, []);

  const restore = useEvent(() => {
    setIsMinimized(false);
    dock?.restoreWindow(windowId);
  });

  const closeFromDock = useEvent(() => {
    setIsMinimized(false);
    // The modal's own close path runs from here; the host (or the effect
    // below, for standalone modals) closes the registry record.
    onClose();
  });

  // Hides instantly — the exact same single-frame teardown as a normal close.
  // (Fading out makes the backdrop's un-blur visible and reads as a flicker.)
  const minimize = useEvent(() => {
    setIsMinimized(true);
    dock?.minimizeWindow({
      icon: getIcon(),
      id: windowId,
      onClose: closeFromDock,
      onRestore: restore,
      title: liveTitleRef.current.trim() || fallbackTitle,
    });
  });

  const closeWindow = dock?.closeWindow;

  // A modal closed while minimized (e.g. its toggle hidden programmatically)
  // must not stay minimized, or reopening the same toggle would render
  // nothing. Standalone modals also close their registry record here; hosted
  // ones leave that to their ModalInstanceHost.
  useEffect(() => {
    if (open) return;

    setIsMinimized(false);

    if (!isHosted) closeWindow?.(windowId);
  }, [closeWindow, isHosted, open, windowId]);

  // A standalone modal that unmounts takes its draft with it.
  useEffect(() => {
    if (isHosted || !canMinimize) return undefined;

    return () => closeWindow?.(windowId);
  }, [canMinimize, closeWindow, isHosted, windowId]);

  // The modal is kept mounted ONLY while actually minimized — a plain close
  // unmounts as usual, so always-rendered modals (e.g. one per timeline item)
  // don't pile up hidden in the DOM.
  const modalProps = {
    disableRestoreFocus: canMinimize && isMinimized,
    // Body scroll-lock stays on while open, like every other modal. The lock
    // releasing on minimize costs a one-frame reflow on classic (non-overlay)
    // scrollbars; accepted, since it only occurs at that explicit action.
    keepMounted: canMinimize && isMinimized,
    open: open && !isMinimized,
  };

  return { canMinimize, isMinimized, minimize, modalProps, reportTitle };
};

export default useMinimizableModal;
